import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ShieldCheck, FileStack, Bookmark, Activity, LogOut, Crown, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { TOOLS } from '../lib/tools';

export default function DashboardPage() {
  const { user, loading, logout } = useAuth();

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-400">Unlocking the vault…</div>;
  if (!user || typeof user !== 'object') return <Navigate to="/login" state={{ from: '/dashboard' }} replace />;

  const availableTools = TOOLS.filter((t) => t.available);

  return (
    <div data-testid="dashboard-page" className="vault-grid-bg min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {/* Welcome */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-mono text-vault-gold">Your Vault</p>
            <h1 data-testid="welcome-heading" className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold tracking-tighter mt-2">
              Welcome, <span className="gold-text">{user.name || user.email.split('@')[0]}</span>
            </h1>
            <p data-testid="user-email" className="text-sm text-gray-400 mt-2 flex items-center gap-2">
              <Mail className="w-3.5 h-3.5" /> {user.email}
            </p>
          </div>
          <button data-testid="dashboard-logout" onClick={logout} className="btn-ghost inline-flex items-center gap-2 self-start">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
          <StatCard
            testId="stat-plan"
            Icon={Crown}
            label="Current Plan"
            value={user.plan === 'pro' ? 'Pro' : 'Free'}
            sub={user.plan === 'pro' ? 'All tools unlocked' : '500MB per file · 30-day history'}
          />
          <StatCard
            testId="stat-tools"
            Icon={FileStack}
            label="Tools Available"
            value={availableTools.length.toString()}
            sub={`${TOOLS.length - availableTools.length} more launching soon`}
          />
          <StatCard
            testId="stat-history"
            Icon={Activity}
            label="File History"
            value="30 days"
            sub="Local browser session"
          />
        </div>

        {/* Quick access */}
        <section data-testid="quick-access">
          <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
            <Bookmark className="w-5 h-5 text-vault-gold" /> Quick access
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {availableTools.map((t) => (
              <Link
                key={t.slug}
                to={`/${t.slug}`}
                data-testid={`quick-${t.slug}`}
                className="tool-card rounded-lg p-4 text-center"
              >
                <div className="text-sm font-semibold text-white">{t.name}</div>
                <div className="text-xs text-gray-500 mt-1">{t.category}</div>
              </Link>
            ))}
          </div>
        </section>

        {/* Upgrade banner */}
        {user.plan !== 'pro' && (
          <section data-testid="upgrade-banner" className="mt-12 glass-panel rounded-2xl p-8 md:p-10 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-vault-gold/20 rounded-full blur-3xl" />
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-5 h-5 text-vault-gold" />
                  <span className="text-xs tracking-[0.2em] uppercase font-mono text-vault-gold">ValutDocu Pro</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-heading font-bold tracking-tight">
                  Unlock AI tools + 2GB files.
                </h3>
                <p className="text-sm text-gray-400 mt-2 max-w-md">
                  AI Chat with PDF, batch processing, priority queue, and no ads. Just ₹149/mo.
                </p>
              </div>
              <Link
                to="/pricing"
                data-testid="upgrade-button"
                className="btn-primary whitespace-nowrap inline-flex items-center gap-2"
              >
                <Crown className="w-4 h-4" /> Start 7-day free trial
              </Link>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function StatCard({ testId, Icon, label, value, sub }) {
  return (
    <div data-testid={testId} className="tool-card rounded-xl p-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-vault-gold" />
        <span className="text-xs tracking-[0.2em] uppercase font-mono text-gray-400">{label}</span>
      </div>
      <div className="text-3xl font-heading font-bold tracking-tighter text-white">{value}</div>
      <p className="text-xs text-gray-500 mt-2">{sub}</p>
    </div>
  );
}
